import { z } from 'zod';
import { customerPortalProjectIntakeTransferAssetsSchema, customerPortalProjectIntakeTransferMappingsSchema, } from './customer-portal-project-intake-asset.contract.js';
export const customerPortalProjectIntakeCapability = 'customer-portal.project-intake-requests.v1';
export const customerPortalProjectIntakeReadGrant = 'customer-portal.project-intake.read';
export const customerPortalProjectIntakeWriteGrant = 'customer-portal.project-intake.write';
export const customerPortalProjectIntakeResourceType = 'customer-portal.project-intake';
export const customerPortalProjectIntakeAcceptanceSlot = 'customer-portal.project-intake-acceptance-actions';
export const customerPortalProjectsIntakeAcceptanceProviderId = 'projects.server.customer-portal-intake-acceptance';
export const customerPortalRelationshipsIntakeAcceptanceProviderId = 'relationships.server.customer-portal-intake-acceptance';
export const customerPortalInsuranceClaimsIntakeAcceptanceProviderId = 'insurance-claims.server.customer-portal-intake-acceptance';
export const customerPortalProjectsIntakeAcceptanceCapability = 'projects.customer-portal-intake-acceptance.v2';
export const customerPortalRelationshipsIntakeAcceptanceCapability = 'relationships.customer-portal-intake-acceptance.v1';
export const customerPortalInsuranceClaimsIntakeAcceptanceCapability = 'insurance-claims.customer-portal-intake-acceptance.v1';
const nullableText = (maximum) => z.string().trim().min(1).max(maximum).nullable();
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const customerPortalProjectIntakeRequestStatusSchema = z.enum([
    'draft', 'sent', 'opened', 'partial', 'submitted', 'accepted', 'archived', 'revoked', 'expired',
]);
export const customerPortalProjectIntakeSubmissionStatusSchema = z.enum([
    'draft', 'submitted', 'accepted', 'archived',
]);
export const customerPortalProjectIntakeCommunicationPreferencesSchema = z.strictObject({
    email: z.boolean(),
    sms: z.boolean(),
    phone: z.boolean(),
    preferredChannel: z.enum(['email', 'sms', 'phone']).nullable(),
});
export const customerPortalProjectIntakeSubmissionInputSchema = z.strictObject({
    incidentType: nullableText(100),
    lossTimestamp: z.string().datetime({ offset: true }).nullable(),
    cause: nullableText(200),
    affectedAreas: z.array(z.string().trim().min(1).max(100)).max(50).superRefine((areas, context) => {
        if (new Set(areas.map((area) => area.toLowerCase())).size !== areas.length) {
            context.addIssue({ code: 'custom', message: 'Affected areas must be unique.' });
        }
    }),
    story: nullableText(5_000),
    customerName: nullableText(200),
    customerEmail: z.string().trim().email().max(320).nullable(),
    customerPhone: nullableText(50),
    insuranceCompany: nullableText(200),
    policyNumber: nullableText(120),
    claimNumber: nullableText(120),
    adjusterName: nullableText(200),
    adjusterEmail: z.string().trim().email().max(320).nullable(),
    adjusterPhone: nullableText(50),
    communicationPreferences: customerPortalProjectIntakeCommunicationPreferencesSchema,
});
export const customerPortalProjectIntakeSubmissionSchema = customerPortalProjectIntakeSubmissionInputSchema.extend({
    id: z.string().uuid(),
    requestId: z.string().uuid(),
    status: customerPortalProjectIntakeSubmissionStatusSchema,
    revision: z.number().int().positive(),
    sourceHash: sha256Schema,
    submittedAt: z.string().datetime({ offset: true }).nullable(),
    acceptedAt: z.string().datetime({ offset: true }).nullable(),
    updatedAt: z.string().datetime({ offset: true }),
});
export const customerPortalProjectIntakeOwnerOutcomeSchema = z.strictObject({
    providerModuleId: z.enum(['projects', 'relationships', 'insurance-claims']),
    state: z.enum(['applied', 'not_applicable', 'unavailable', 'not_authorized']),
    factId: z.string().uuid().nullable(),
    idempotent: z.boolean(),
});
export const customerPortalProjectIntakeRequestSchema = z.strictObject({
    contractVersion: z.literal(customerPortalProjectIntakeCapability),
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    currentLinkId: z.string().uuid(),
    recipientName: nullableText(200),
    recipientEmail: z.string().trim().email().max(320).nullable(),
    recipientPhone: nullableText(50),
    title: nullableText(200),
    introMessage: nullableText(4_000),
    status: customerPortalProjectIntakeRequestStatusSchema,
    revision: z.number().int().positive(),
    expiresAt: z.string().datetime({ offset: true }),
    createdByMembershipId: z.string().uuid(),
    lastOpenedAt: z.string().datetime({ offset: true }).nullable(),
    submittedAt: z.string().datetime({ offset: true }).nullable(),
    acceptedAt: z.string().datetime({ offset: true }).nullable(),
    acceptedByMembershipId: z.string().uuid().nullable(),
    acceptanceOutcomes: z.array(customerPortalProjectIntakeOwnerOutcomeSchema).max(3),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    submission: customerPortalProjectIntakeSubmissionSchema,
});
export const customerPortalCreateProjectIntakeSchema = z.strictObject({
    commandId: z.string().uuid(),
    projectId: z.string().uuid(),
    recipientName: nullableText(200).default(null),
    recipientEmail: z.string().trim().email().max(320).nullable().default(null),
    recipientPhone: nullableText(50).default(null),
    title: nullableText(200).default(null),
    introMessage: nullableText(4_000).default(null),
    expiresAt: z.string().datetime({ offset: true }),
});
export const customerPortalRotateProjectIntakeSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    expiresAt: z.string().datetime({ offset: true }),
});
export const customerPortalRevokeProjectIntakeSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(3).max(500),
});
export const customerPortalAcceptProjectIntakeSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(3).max(500),
});
export const customerPortalProjectIntakeListResponseSchema = z.strictObject({
    data: z.array(customerPortalProjectIntakeRequestSchema),
});
export const customerPortalProjectIntakeReadResponseSchema = z.strictObject({
    data: customerPortalProjectIntakeRequestSchema,
});
export const customerPortalProjectIntakeResponseSchema = z.strictObject({
    data: customerPortalProjectIntakeRequestSchema,
    idempotent: z.boolean(),
});
export const customerPortalProjectIntakeLinkResponseSchema = z.strictObject({
    data: customerPortalProjectIntakeRequestSchema,
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/).nullable(),
    urlPath: z.string().startsWith('/p/').nullable(),
    idempotent: z.boolean(),
});
export const customerPortalPublicProjectIntakeResponseSchema = z.strictObject({
    contractVersion: z.literal(customerPortalProjectIntakeCapability),
    data: customerPortalProjectIntakeRequestSchema.omit({
        currentLinkId: true,
        createdByMembershipId: true,
        acceptedByMembershipId: true,
        acceptanceOutcomes: true,
    }),
});
export const customerPortalPublicProjectIntakeCommandSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedSubmissionRevision: z.number().int().positive(),
    submission: customerPortalProjectIntakeSubmissionInputSchema,
});
export const customerPortalPublicProjectIntakeCommandResponseSchema = z.strictObject({
    contractVersion: z.literal(customerPortalProjectIntakeCapability),
    data: customerPortalProjectIntakeSubmissionSchema,
    requestStatus: customerPortalProjectIntakeRequestStatusSchema,
    requestRevision: z.number().int().positive(),
    idempotent: z.boolean(),
});
const acceptanceBaseSchema = z.strictObject({
    consumerModuleId: z.literal('customer-portal'),
    sourceCapability: z.literal(customerPortalProjectIntakeCapability),
    commandId: z.string().uuid(),
    projectId: z.string().uuid(),
    intakeRequestId: z.string().uuid(),
    submissionId: z.string().uuid(),
    submissionRevision: z.number().int().positive(),
    submissionHash: sha256Schema,
});
export const customerPortalProjectsIntakeAcceptanceRequestSchema = acceptanceBaseSchema.extend({
    requestedCapability: z.literal(customerPortalProjectsIntakeAcceptanceCapability),
    incident: customerPortalProjectIntakeSubmissionInputSchema.pick({
        incidentType: true, lossTimestamp: true, cause: true, affectedAreas: true, story: true,
        customerName: true, customerEmail: true, customerPhone: true,
    }),
    assets: customerPortalProjectIntakeTransferAssetsSchema,
});
export const customerPortalRelationshipsIntakeAcceptanceRequestSchema = acceptanceBaseSchema.extend({
    requestedCapability: z.literal(customerPortalRelationshipsIntakeAcceptanceCapability),
    customer: customerPortalProjectIntakeSubmissionInputSchema.pick({
        customerName: true, customerEmail: true, customerPhone: true, communicationPreferences: true,
    }),
});
export const customerPortalInsuranceClaimsIntakeAcceptanceRequestSchema = acceptanceBaseSchema.extend({
    requestedCapability: z.literal(customerPortalInsuranceClaimsIntakeAcceptanceCapability),
    insurance: customerPortalProjectIntakeSubmissionInputSchema.pick({
        insuranceCompany: true, policyNumber: true, claimNumber: true,
        adjusterName: true, adjusterEmail: true, adjusterPhone: true,
    }),
});
const customerPortalIntakeAcceptanceProviderResponseBaseSchema = z.strictObject({
    state: z.literal('accepted'),
    capability: z.enum([
        customerPortalRelationshipsIntakeAcceptanceCapability,
        customerPortalInsuranceClaimsIntakeAcceptanceCapability,
    ]),
    tenantId: z.string().uuid(),
    projectId: z.string().uuid(),
    intakeRequestId: z.string().uuid(),
    factId: z.string().uuid(),
    idempotent: z.boolean(),
});
export const customerPortalIntakeAcceptanceProviderResponseSchema = customerPortalIntakeAcceptanceProviderResponseBaseSchema;
export const customerPortalProjectsIntakeAcceptanceResponseSchema = customerPortalIntakeAcceptanceProviderResponseBaseSchema.extend({
    capability: z.literal(customerPortalProjectsIntakeAcceptanceCapability),
    transferredFiles: customerPortalProjectIntakeTransferMappingsSchema,
});
//# sourceMappingURL=customer-portal-project-intake.contract.js.map